import { Link } from 'react-router-dom';
import { Mail, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';

const Footer = ({ config, overlap = true }) => {
  const quickLinks = [
    { label: 'Home', url: '/' },
    { label: 'Accountability', url: '/accountability', hasDropdown: true },
    { label: 'Legislation', url: '/legislation', hasDropdown: true },
    { label: 'Citizen Action', url: '/citizen-action' },
    { label: 'Resources', url: '/resources', hasDropdown: true },
    { label: 'News', url: '/news', hasDropdown: true },
    { label: 'Contact Us', url: '/contact-us' }
  ];

  const socialLinks = [
    { icon: 'fa-x-twitter', label: 'X', url: 'https://x.com/securethevotemd' },
    { icon: 'fa-instagram', label: 'Instagram', url: 'https://www.instagram.com/securethevotemd/' },
    { icon: 'fa-facebook-f', label: 'Facebook', url: 'https://www.facebook.com/profile.php?id=61558958704794' },
    { icon: 'fa-tiktok', label: 'TikTok', url: 'https://www.tiktok.com/@securethevotemd?lang=en' },
    { icon: 'fa-t', label: 'Truth Social', url: 'https://truthsocial.com/@securethevotemd', isSvg: true, svgPath: '/icons/truth-social.svg' },
    { icon: 'fa-g', label: 'Getter', url: 'https://gettr.com/user/securethevotemd', isSvg: true, svgPath: '/icons/gettr.svg' },
    { icon: 'fa-youtube', label: 'YouTube', url: 'https://www.youtube.com/@SecuretheVoteMD' }
  ];

  return (
    <footer data-testid="site-footer" className="relative" style={{ marginTop: overlap ? '-60px' : 0, zIndex: 10 }}>
      {/* Main Footer with American flag background */}
      <div 
        className="pt-24 pb-16 relative overflow-hidden"
        style={{ 
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_a665581c-5436-4954-9e68-bc954333d72c/artifacts/xm7bw5nb_2026-04-10%2012.07.25.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'left center'
        }}
      >
        {/* Gradient overlay */}
        <div 
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(to right, rgba(139, 21, 56, 0.2) 0%, rgba(139, 21, 56, 0.6) 30%, rgba(139, 21, 56, 0.95) 55%, rgba(139, 21, 56, 1) 70%),
              linear-gradient(to bottom, rgba(139, 21, 56, 0.2) 0%, rgba(139, 21, 56, 0.5) 35%, rgba(139, 21, 56, 0.85) 60%, rgba(139, 21, 56, 1) 80%)
            `
          }}
        />
        
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 items-start">
            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h4 className="text-lg font-bold text-[#F6BF58] uppercase tracking-wider mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Quick Links
              </h4>
              <nav aria-label="Footer navigation" className="flex flex-col gap-2">
                {quickLinks.map((link, index) => (
                  <Link
                    key={index}
                    to={link.url}
                    className="text-white/90 hover:text-white text-sm flex items-center gap-1 transition-colors"
                    data-testid={`footer-link-${index}`}
                  >
                    {link.label}
                    {link.hasDropdown && <ChevronDown size={14} />}
                  </Link>
                ))}
              </nav>
            </motion.div>

            {/* Center Logo Card — rises up from bottom */}
            <motion.div
              className="flex justify-center"
              initial={{ opacity: 0, y: 80 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              viewport={{ once: true, margin: "-50px" }}
            >
              <div className="rounded-lg p-6 text-center max-w-xs shadow-xl" style={{ backgroundColor: '#FDE3AF' }}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_secure-vote-homepage/artifacts/fv4hr1qo_LOGOsecurethevote_yellowMD.png" 
                  alt="Secure the Vote Maryland"
                  className="h-16 mx-auto mb-4"
                />
                <p className="text-slate-700 text-sm leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Secure and Accurate elections are necessary for a government of the people, by the people.
                </p>
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              className="md:text-right"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h4 className="text-lg font-bold text-[#F6BF58] uppercase tracking-wider mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Contact Info
              </h4>
              <div className="flex flex-col gap-3">
                <a
                  href={`mailto:${config?.contact_email || 'info@securethevotemd.com'}`}
                  className="text-white/90 hover:text-white text-sm flex items-center gap-2 md:justify-end"
                  data-testid="footer-email"
                >
                  <Mail size={16} className="text-[#F6BF58]" />
                  {config?.contact_email || 'info@securethevotemd.com'}
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-[#6B1029] py-4">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/70 text-sm" data-testid="footer-copyright">
              {config?.copyright_text || '© 2025 Secure The Vote MD. All rights reserved.'}
            </p>
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-white/50 hover:text-white/70 text-xs transition-colors" data-testid="footer-admin-login">
                Admin Login
              </Link>
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#F6BF58] hover:text-white transition-colors"
                  aria-label={social.label}
                  data-testid={`footer-social-${index}`}
                >
                  {social.isSvg ? (
                    <img src={`${process.env.PUBLIC_URL || ""}${social.svgPath}`} alt={social.label} className="h-[15px] w-auto" style={{ filter: 'brightness(0) saturate(100%) invert(80%) sepia(30%) saturate(700%) hue-rotate(5deg) brightness(90%)' }} />
                  ) : (
                    <i className={`fa-brands ${social.icon}`}></i>
                  )}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
